import "./MarketDataCard.css";

const API_URL = "https://api.ayesearch.com/api/market-data/search/";

interface MarketSecurity {
  id: number;
  symbol: string;
  name: string;
  exchange: string;
  country: string;
  currency: string;
  logo_url: string;
  website: string;
  sector: string;
  sub_sector: string;
  market_classification: string;
  shares_outstanding: number | null;
}

interface MarketQuote {
  price: number | null;
  previous_close: number | null;
  day_high: number | null;
  day_low: number | null;
  volume: number | null;
  market_cap: number | null;
  display_market_cap: string;
  price_change: number | null;
  price_change_percent: number | null;
  change_7d_percent: number | null;
  change_52w_percent: number | null;
  change_1m_percent: number | null;
  change_ytd_percent: number | null;
  high_52wk: number | null;
  low_52wk: number | null;
  provider_updated_at: string | null;
  fetched_at: string | null;
  checked_at?: string | null;
}

interface MarketPoint {
  timestamp: string;
  price: number;
  volume?: number | null;
  market_cap?: number | null;
}

interface MarketDataResponse {
  kind?: "market_data";
  provider?: string;
  security?: MarketSecurity;
  quote?: MarketQuote | null;
  chart?: {
    source?: string;
    points?: MarketPoint[];
    ranges?: Record<string, MarketPoint[]>;
  };
  source?: {
    name?: string;
    url?: string;
    attribution?: string;
  };
  error?: string;
}

type ChartRange = "1d" | "5d" | "1m" | "6m" | "ytd" | "1y";

const CHART_RANGES: ChartRange[] = [
  "1d",
  "5d",
  "1m",
  "6m",
  "ytd",
  "1y",
];

function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function money(
  value: number | null | undefined,
  currency = "NGN",
): string {
  if (
    value == null ||
    !Number.isFinite(Number(value))
  ) {
    return "—";
  }

  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function chartMoney(
  value: number,
  currency = "NGN",
): string {
  if (!Number.isFinite(value)) {
    return "—";
  }

  const absolute = Math.abs(value);
  const fractionDigits =
    absolute >= 1000
      ? 0
      : absolute >= 100
        ? 1
        : 2;

  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function compactNumber(
  value: number | null | undefined,
): string {
  if (
    value == null ||
    !Number.isFinite(Number(value))
  ) {
    return "—";
  }

  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function formatMarketTimestamp(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null;
  }

  const when = new Date(value);

  if (!Number.isFinite(when.getTime())) {
    return null;
  }

  return when.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function updatedLabel(
  value: string | null | undefined,
): string {
  const formatted = formatMarketTimestamp(value);
  return formatted
    ? `Updated ${formatted}`
    : "Provider update time unavailable";
}

function checkedLabel(
  value: string | null | undefined,
): string {
  const formatted = formatMarketTimestamp(value);
  return formatted
    ? `Checked by AyeSearch ${formatted}`
    : "AyeSearch check time unavailable";
}

function formatXAxisLabel(
  timestamp: string,
  range: ChartRange,
): string {
  const date = new Date(timestamp);

  if (!Number.isFinite(date.getTime())) {
    return "";
  }

  if (range === "1d") {
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  if (range === "1y") {
    return date.toLocaleDateString([], {
      month: "short",
      year: "2-digit",
    });
  }

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
}

function chooseXAxisIndexes(
  length: number,
): number[] {
  if (length <= 0) {
    return [];
  }

  if (length === 1) {
    return [0];
  }

  const wanted = Math.min(
    5,
    length,
  );

  const indexes =
    new Set<number>();

  for (
    let slot = 0;
    slot < wanted;
    slot += 1
  ) {
    indexes.add(
      Math.round(
        (
          slot /
          Math.max(
            1,
            wanted - 1,
          )
        ) *
          (length - 1),
      ),
    );
  }

  return Array.from(
    indexes,
  ).sort(
    (a, b) => a - b,
  );
}

function lineChart(
  points: MarketPoint[],
  currency: string,
  range: ChartRange,
): string {
  const usable = points
    .filter(
      (point) =>
        Number.isFinite(
          Number(point.price),
        ) &&
        Number.isFinite(
          new Date(
            point.timestamp,
          ).getTime(),
        ),
    )
    .sort(
      (a, b) =>
        new Date(
          a.timestamp,
        ).getTime() -
        new Date(
          b.timestamp,
        ).getTime(),
    );

  if (usable.length < 2) {
    return `
      <div class="ngx-chart-empty">
        <strong>Price chart is building</strong>
        <span>
          AyeSearch will draw this chart as cached NGN Market snapshots accumulate.
        </span>
      </div>
    `;
  }

  const width = 760;
  const height = 260;

  const plotLeft = 78;
  const plotRight = 16;
  const plotTop = 18;
  const plotBottom = 44;

  const plotWidth =
    width -
    plotLeft -
    plotRight;

  const plotHeight =
    height -
    plotTop -
    plotBottom;

  const prices =
    usable.map(
      (point) =>
        Number(
          point.price,
        ),
    );

  const rawMin =
    Math.min(...prices);

  const rawMax =
    Math.max(...prices);

  const baseRange =
    Math.max(
      rawMax - rawMin,
      Math.abs(rawMax) *
        0.01,
      1,
    );

  const verticalPadding =
    baseRange * 0.16;

  let min =
    rawMin -
    verticalPadding;

  let max =
    rawMax +
    verticalPadding;

  if (
    rawMin >= 0 &&
    min < 0
  ) {
    min = 0;
  }

  if (min === max) {
    min -= 1;
    max += 1;
  }

  const xForIndex = (
    index: number,
  ) =>
    plotLeft +
    (
      index /
      Math.max(
        1,
        usable.length - 1,
      )
    ) *
      plotWidth;

  const yForPrice = (
    price: number,
  ) =>
    plotTop +
    (
      1 -
      (
        price -
        min
      ) /
        (
          max -
          min
        )
    ) *
      plotHeight;

  const coords =
    usable.map(
      (
        point,
        index,
      ) => {
        const x =
          xForIndex(
            index,
          );

        const y =
          yForPrice(
            Number(
              point.price,
            ),
          );

        return `${x.toFixed(
          2,
        )},${y.toFixed(
          2,
        )}`;
      },
    );

  const yTicks =
    Array.from(
      {
        length: 5,
      },
      (
        _,
        index,
      ) => {
        const ratio =
          index / 4;

        return {
          value:
            max -
            ratio *
              (
                max -
                min
              ),
          y:
            plotTop +
            ratio *
              plotHeight,
        };
      },
    );

  const xIndexes =
    chooseXAxisIndexes(
      usable.length,
    );

  return `
    <div class="ngx-chart-wrap">
      <svg
        class="ngx-chart"
        viewBox="0 0 ${width} ${height}"
        role="img"
        aria-label="${esc(
          range.toUpperCase(),
        )} cached price chart"
        preserveAspectRatio="none"
      >
        <g class="ngx-chart-grid">
          ${yTicks
            .map(
              (tick) => `
                <line
                  x1="${plotLeft}"
                  y1="${tick.y.toFixed(
                    2,
                  )}"
                  x2="${
                    width -
                    plotRight
                  }"
                  y2="${tick.y.toFixed(
                    2,
                  )}"
                  vector-effect="non-scaling-stroke"
                />
              `,
            )
            .join("")}

          ${xIndexes
            .map(
              (
                index,
              ) => {
                const x =
                  xForIndex(
                    index,
                  );

                return `
                  <line
                    class="ngx-chart-grid-vertical"
                    x1="${x.toFixed(
                      2,
                    )}"
                    y1="${plotTop}"
                    x2="${x.toFixed(
                      2,
                    )}"
                    y2="${
                      height -
                      plotBottom
                    }"
                    vector-effect="non-scaling-stroke"
                  />
                `;
              },
            )
            .join("")}
        </g>

        <line
          class="ngx-chart-axis-line"
          x1="${plotLeft}"
          y1="${plotTop}"
          x2="${plotLeft}"
          y2="${
            height -
            plotBottom
          }"
          vector-effect="non-scaling-stroke"
        />

        <line
          class="ngx-chart-axis-line"
          x1="${plotLeft}"
          y1="${
            height -
            plotBottom
          }"
          x2="${
            width -
            plotRight
          }"
          y2="${
            height -
            plotBottom
          }"
          vector-effect="non-scaling-stroke"
        />

        <polyline
          class="ngx-chart-line"
          points="${coords.join(
            " ",
          )}"
          fill="none"
          vector-effect="non-scaling-stroke"
        />

        ${usable
          .map(
            (
              point,
              index,
            ) => {
              const x =
                xForIndex(
                  index,
                );

              const y =
                yForPrice(
                  Number(
                    point.price,
                  ),
                );

              return `
                <circle
                  class="ngx-chart-point"
                  cx="${x.toFixed(
                    2,
                  )}"
                  cy="${y.toFixed(
                    2,
                  )}"
                  r="2.7"
                  vector-effect="non-scaling-stroke"
                >
                  <title>${esc(
                    `${formatXAxisLabel(
                      point.timestamp,
                      range,
                    )} — ${money(
                      Number(
                        point.price,
                      ),
                      currency,
                    )}`,
                  )}</title>
                </circle>
              `;
            },
          )
          .join("")}
      </svg>

      <div
        class="ngx-chart-y-labels"
        aria-hidden="true"
      >
        ${yTicks
          .map(
            (
              tick,
            ) => `
              <span
                style="top:${(
                  (
                    tick.y /
                    height
                  ) *
                  100
                ).toFixed(
                  3,
                )}%"
              >
                ${esc(
                  chartMoney(
                    tick.value,
                    currency,
                  ),
                )}
              </span>
            `,
          )
          .join("")}
      </div>

      <div
        class="ngx-chart-x-labels"
        aria-hidden="true"
      >
        ${xIndexes
          .map(
            (
              index,
            ) => {
              const point =
                usable[
                  index
                ];

              const x =
                xForIndex(
                  index,
                );

              const percent =
                (
                  x /
                  width
                ) *
                100;

              const transform =
                index === 0
                  ? "translateX(0)"
                  : index ===
                      usable.length -
                        1
                    ? "translateX(-100%)"
                    : "translateX(-50%)";

              return `
                <span
                  class="${
                    index === 0
                      ? "first"
                      : index === usable.length - 1
                        ? "last"
                        : ""
                  }"
                  style="
                    left:${percent.toFixed(
                      3,
                    )}%;
                    transform:${transform};
                  "
                >
                  ${esc(
                    formatXAxisLabel(
                      point.timestamp,
                      range,
                    ),
                  )}
                </span>
              `;
            },
          )
          .join("")}
      </div>

      <div
        class="ngx-chart-axis-title ngx-chart-axis-title-y"
      >
        Price
      </div>

      <div
        class="ngx-chart-axis-title ngx-chart-axis-title-x"
      >
        ${
          range === "1d"
            ? "Time"
            : range === "1y"
              ? "Month"
              : "Date"
        }
      </div>
    </div>
  `;
}

function stat(
  label: string,
  value: string,
): string {
  return `
    <div class="ngx-market-stat">
      <span>${esc(
        label,
      )}</span>
      <strong>${esc(
        value,
      )}</strong>
    </div>
  `;
}

function renderCard(
  container: HTMLElement,
  data: MarketDataResponse,
  selectedRange: ChartRange = "1m",
): void {
  const security =
    data.security;

  const quote =
    data.quote;

  if (
    !security ||
    !quote
  ) {
    container.innerHTML =
      "";
    return;
  }

  const ranges =
    data.chart?.ranges ||
    {};

  const points =
    Array.isArray(
      ranges[
        selectedRange
      ],
    )
      ? ranges[
          selectedRange
        ]
      : data.chart
          ?.points ||
        [];

  const change =
    quote.price_change_percent;

  const changeClass =
    change == null
      ? ""
      : change > 0
        ? "positive"
        : change < 0
          ? "negative"
          : "neutral";

  container.innerHTML = `
    <section class="ngx-market-card">
      <header class="ngx-market-header">
        <div class="ngx-market-identity">
          ${
            security.logo_url
              ? `<img src="${esc(
                  security.logo_url,
                )}" alt="" loading="eager" referrerpolicy="no-referrer" />`
              : ""
          }

          <div>
            <h1>${esc(
              security.name,
            )}</h1>

            <div class="ngx-market-symbol">
              ${esc(
                security.exchange,
              )}: ${esc(
                security.symbol,
              )}
            </div>

            ${
              security.sector
                ? `<div class="ngx-market-sector">${esc(
                    security.sector,
                  )}</div>`
                : ""
            }
          </div>
        </div>
      </header>

      <div class="ngx-market-cap">
        ${esc(
          quote.display_market_cap ||
            compactNumber(
              quote.market_cap,
            ),
        )}
      </div>

      <div class="ngx-market-cap-label">
        Market capitalization
      </div>

      <div class="ngx-market-price-row">
        <strong>
          ${esc(
            money(
              quote.price,
              security.currency ||
                "NGN",
            ),
          )}
        </strong>

        ${
          change != null
            ? `
              <span class="${changeClass}">
                ${
                  change >
                  0
                    ? "+"
                    : ""
                }${esc(
                  change.toFixed(
                    2,
                  ),
                )}% today
              </span>
            `
            : ""
        }
      </div>

      <div class="ngx-market-freshness" aria-label="Market data freshness">
        <div class="ngx-market-updated">
          ${esc(
            updatedLabel(
              quote.provider_updated_at,
            ),
          )}
        </div>

        <div class="ngx-market-checked">
          ${esc(
            checkedLabel(
              quote.checked_at ||
                quote.fetched_at,
            ),
          )}
        </div>
      </div>

      <div
        class="ngx-market-range-tabs"
        role="tablist"
        aria-label="Chart range"
      >
        ${CHART_RANGES
          .map(
            (
              range,
            ) => `
              <button
                type="button"
                class="${
                  range ===
                  selectedRange
                    ? "active"
                    : ""
                }"
                data-market-range="${range}"
                role="tab"
                aria-selected="${
                  range ===
                  selectedRange
                    ? "true"
                    : "false"
                }"
              >
                ${range.toUpperCase()}
              </button>
            `,
          )
          .join("")}
      </div>

      <div class="ngx-market-chart-host">
        ${lineChart(
          points,
          security.currency ||
            "NGN",
          selectedRange,
        )}
      </div>

      <div class="ngx-market-stats">
        ${stat(
          "Previous close",
          money(
            quote.previous_close,
            security.currency,
          ),
        )}

        ${stat(
          "Day range",
          quote.day_low !=
              null ||
            quote.day_high !=
              null
            ? `${money(
                quote.day_low,
                security.currency,
              )} – ${money(
                quote.day_high,
                security.currency,
              )}`
            : "Unavailable",
        )}

        ${stat(
          "52-week range",
          quote.low_52wk !=
              null ||
            quote.high_52wk !=
              null
            ? `${money(
                quote.low_52wk,
                security.currency,
              )} – ${money(
                quote.high_52wk,
                security.currency,
              )}`
            : "Unavailable",
        )}

        ${stat(
          "Volume",
          compactNumber(
            quote.volume,
          ),
        )}

        ${stat(
          "Market cap",
          quote.display_market_cap ||
            compactNumber(
              quote.market_cap,
            ),
        )}

        ${stat(
          "YTD change",
          quote.change_ytd_percent ==
          null
            ? "—"
            : `${
                quote.change_ytd_percent >
                0
                  ? "+"
                  : ""
              }${quote.change_ytd_percent.toFixed(
                2,
              )}%`,
        )}
      </div>

      <footer class="ngx-market-source">
        <span>
          AyeSearch cached market data
        </span>

        ${
          data.source?.url
            ? `<a
                href="${esc(
                  data.source.url,
                )}"
                target="_blank"
                rel="noopener noreferrer"
              >${esc(
                data.source
                  .attribution ||
                  data.source
                    .name ||
                  "NGN Market",
              )}</a>`
            : `<span>${esc(
                data.source
                  ?.attribution ||
                  "Data provided by NGN Market",
              )}</span>`
        }
      </footer>
    </section>
  `;

  container
    .querySelectorAll<HTMLButtonElement>(
      "[data-market-range]",
    )
    .forEach(
      (
        button,
      ) => {
        button.addEventListener(
          "click",
          () => {
            const next =
              button.dataset
                .marketRange as
                | ChartRange
                | undefined;

            if (!next) {
              return;
            }

            renderCard(
              container,
              data,
              next,
            );
          },
        );
      },
    );
}

export function looksLikeMarketDataQuery(
  query: string,
): boolean {
  const q =
    String(
      query ||
        "",
    )
      .toLowerCase()
      .trim();

  return (
    /\bmarket\s*cap(?:italization)?\b/.test(
      q,
    ) ||
    /\bmarket\s+capitalisation\b/.test(
      q,
    ) ||
    /\bshare\s+price\b/.test(
      q,
    ) ||
    /\bstock\s+price\b/.test(
      q,
    )
  );
}

export async function renderMarketDataCard(
  container: HTMLElement,
  query: string,
): Promise<boolean> {
  const url =
    new URL(
      API_URL,
    );

  url.searchParams.set(
    "q",
    query,
  );

  try {
    const response =
      await fetch(
        url.toString(),
        {
          method: "GET",
          credentials:
            "include",
          headers: {
            Accept:
              "application/json",
          },
        },
      );

    const contentType =
      response.headers.get(
        "content-type",
      ) ||
      "";

    if (
      !contentType.includes(
        "application/json",
      )
    ) {
      return false;
    }

    const data =
      (await response.json()) as MarketDataResponse;

    if (
      response.status ===
      404
    ) {
      return false;
    }

    if (
      !response.ok ||
      data.error
    ) {
      throw new Error(
        data.error ||
          `Market data API returned HTTP ${response.status}`,
      );
    }

    if (
      data.kind !==
        "market_data" ||
      !data.security ||
      !data.quote
    ) {
      return false;
    }

    renderCard(
      container,
      data,
    );

    return true;
  } catch (
    error
  ) {
    console.error(
      "AyeSearch NGX market-data card failed:",
      error,
    );

    return false;
  }
}
